<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ $t("STOCK.TRANSFER_ORDERS.TITLE") }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab">
          <v-btn
            v-if="hasPermissions('stock-transfer-orders-create')"
            class="mr-4 success"
            :disabled="isProcessing"
            @click="$router.push({ name: 'stock-transfer-orders-create' })"
          >
            {{ $t("STOCK.TRANSFER_ORDERS.NEW") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text class="pb-0 pt-3">
              <div class="caption grey--text text--darken-1">
                {{ $t("STOCK.TRANSFER_ORDERS.LIST_SUBTITLE") }}
              </div>
              <div
                v-if="actionableTotal > 0"
                class="d-flex flex-wrap mt-2"
              >
                <v-chip
                  v-if="actionableSummary.to_ship > 0"
                  class="mr-2 mb-1"
                  small
                  color="blue"
                  dark
                >
                  {{ $t("STOCK.TRANSFER_ORDERS.ACTIONABLE_TO_SHIP") }}:
                  {{ actionableSummary.to_ship }}
                </v-chip>
                <v-chip
                  v-if="actionableSummary.to_receive > 0"
                  class="mr-2 mb-1"
                  small
                  color="orange"
                  dark
                >
                  {{ $t("STOCK.TRANSFER_ORDERS.ACTIONABLE_TO_RECEIVE") }}:
                  {{ actionableSummary.to_receive }}
                </v-chip>
              </div>
            </v-card-text>
            <v-card-text class="pa-0">
              <Table
                v-if="userStatePrepared"
                ref="stock-transfer-orders-table"
                height="500"
                name="stock-transfer-orders-table"
                :headers="headers"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="25"
                :loading="isProcessing"
                :filters="tableFilters"
                dense
                fixed-header
                filterable
              >
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <template v-if="header.value === 'code'">
                    <router-link
                      :key="index"
                      :to="{ name: 'stock-transfer-orders-view', params: { id: item.item.id } }"
                    >
                      {{ item.item.code }}
                    </router-link>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip :key="index" small :color="statusColor(item.item.status)" dark>
                      {{ statusLabel(item.item.status) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'organization.name'">
                    {{ organizationLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'source_warehouse.label'">
                    {{ warehouseLabel(item.item.source_warehouse) }}
                  </template>
                  <template v-else-if="header.value === 'destination_warehouse.label'">
                    {{ warehouseLabel(item.item.destination_warehouse) }}
                  </template>
                  <template v-else>
                    {{ item.value != null && item.value !== "" ? item.value : "-" }}
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import {
  GET_STOCK_TRANSFER_ORDERS,
  GET_STOCK_TRANSFER_ORDER_ACTIONABLE_SUMMARY,
} from "@/store/stock/stockTransferOrders.module";
import { mapGetters } from "vuex";

const STATUS_OPTIONS = ["draft", "confirmed", "shipped", "received", "closed", "cancelled"];

export default {
  name: "StockListTransferOrders",
  components: { Table },
  data() {
    return {
      tab: "tab-1",
      userStatePrepared: false,
      isProcessing: false,
      tableItemsMethod: GET_STOCK_TRANSFER_ORDERS,
      tableFilters: [],
      headers: [
        {
          text: this.$t("STOCK.TRANSFER_ORDERS.CODE"),
          value: "code",
          sortable: true,
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("GENERAL.LABELS.STATUS"),
          value: "status",
          sortable: false,
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("STOCK.WAREHOUSES.ORGANIZATION"),
          value: "organization.name",
          sortable: false,
          align: "center",
          width: "14%",
        },
        {
          text: this.$t("STOCK.TRANSFER_ORDERS.SOURCE_WAREHOUSE"),
          value: "source_warehouse.label",
          sortable: false,
          align: "center",
          width: "16%",
        },
        {
          text: this.$t("STOCK.TRANSFER_ORDERS.DESTINATION_WAREHOUSE"),
          value: "destination_warehouse.label",
          sortable: false,
          align: "center",
          width: "16%",
        },
        {
          text: this.$t("STOCK.TRANSFER_ORDERS.REQUESTED_DATE"),
          value: "requested_date",
          sortable: true,
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("STOCK.TRANSFER_ORDERS.ITEMS_COUNT"),
          value: "items_count",
          sortable: false,
          align: "center",
          width: "10%",
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      "stockContextVersion",
      "stockTransferOrderActionableSummary",
      "stockTransferOrderActionableTotal",
    ]),
    actionableSummary() {
      return (
        this.stockTransferOrderActionableSummary || {
          to_ship: 0,
          to_receive: 0,
          total: 0,
        }
      );
    },
    actionableTotal() {
      return Number(this.stockTransferOrderActionableTotal || 0);
    },
  },
  watch: {
    stockContextVersion() {
      this.reloadTable();
      this.loadActionableSummary();
    },
  },
  mounted() {
    this.tableFilters = [
      {
        key: "status",
        label: this.$t("GENERAL.LABELS.STATUS"),
        type: "select",
        "item-value": "id",
        "item-text": "label",
        items: STATUS_OPTIONS.map((status) => ({
          id: status,
          label: this.statusLabel(status),
        })),
      },
      {
        key: "requested_date_from",
        label: this.$t("STOCK.TRANSFER_ORDERS.REQUESTED_DATE_FROM"),
        type: "date",
      },
      {
        key: "requested_date_to",
        label: this.$t("STOCK.TRANSFER_ORDERS.REQUESTED_DATE_TO"),
        type: "date",
      },
    ];
    this.userStatePrepared = true;
    this.loadActionableSummary();
  },
  methods: {
    loadActionableSummary() {
      this.$store.dispatch(GET_STOCK_TRANSFER_ORDER_ACTIONABLE_SUMMARY).catch(() => {});
    },
    statusColor(status) {
      const map = {
        draft: "grey",
        confirmed: "blue",
        shipped: "orange",
        received: "teal",
        closed: "success",
        cancelled: "error",
      };
      return map[status] || "grey";
    },
    statusLabel(status) {
      const key = `STOCK.STATUSES.TRANSFER_ORDER.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status || "-";
    },
    organizationLabel(row) {
      const organization = row && row.organization;
      if (!organization) {
        return "-";
      }
      return organization.name || organization.code || "-";
    },
    warehouseLabel(warehouse) {
      if (!warehouse) {
        return "-";
      }
      if (warehouse.code && warehouse.name) {
        return `${warehouse.code} — ${warehouse.name}`;
      }
      return warehouse.label || warehouse.name || warehouse.code || "-";
    },
    reloadTable() {
      this.$nextTick(() => {
        this.$refs["stock-transfer-orders-table"]?.fetchServerData();
      });
    },
  },
};
</script>
